// ============================================================================
// BedsideTestsForm — F. Bedside tests, in the CNE editor between Vitals (E)
// and Examination (G).
//
// These are point-of-care tests the doctor performs at the bedside DURING
// the encounter — urine dipstick, capillary glucose, 12-lead ECG, urine HCG,
// rapid strep, etc. They are:
//
//   • Catalogue-driven (BedsideTest model — global, ~16 seeded entries)
//   • Billable: each row carries a SNAPSHOT of unit_price_pgk resolved
//     server-side from the per-org rate card or catalogue indicative price
//   • Tagged `is_screening_eligible` — folded into corporate medical
//     screening tallies in the org admin dashboard (Phase 2)
//
// UX: catalogue autocomplete → select test → row appears with result input,
// abnormal flag, optional notes, and a read-only price chip. Existing
// records render at the top with their result + a billable badge.
// ============================================================================

import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Card, CardHeader } from '../../components/Card.jsx'
import ErrorBanner from '../../components/ErrorBanner.jsx'
import CatalogueAutocomplete from './CatalogueAutocomplete.jsx'
import { searchBedsideTests } from '../../api/catalogues.js'
import {
  createBedsideTestRecord,
  updateBedsideTestRecord,
  deleteBedsideTestRecord,
} from '../../api/bedsideTests.js'
import { formatDateTime } from '../../utils/format.js'

// Tiny PGK formatter — Decimals come back as strings from DRF; coerce safely.
function fmtPGK(value) {
  if (value == null || value === '') return null
  const n = Number(value)
  if (!Number.isFinite(n)) return null
  return `PGK ${n.toFixed(2)}`
}

export default function BedsideTestsForm({ encounter, locked }) {
  const qc = useQueryClient()
  const [formError, setFormError] = useState(null)

  // Optimistically splice the new row into the encounter cache from the
  // POST response so it appears instantly; then invalidate so any side
  // effects (audit log notifications, etc.) catch up. The cache key is
  // coerced to String(encounter.id) so it matches EncounterEditor's
  // useQuery key (which comes from useParams() and is a string) — without
  // this, the optimistic splice writes to a different slot and rows
  // appear only after the refetch lands.
  const create = useMutation({
    mutationFn: ({ catalogId, result_value, abnormal, notes }) =>
      createBedsideTestRecord(encounter.id, {
        catalog: catalogId, result_value, abnormal, notes,
      }),
    onSuccess: (newRecord) => {
      setFormError(null)
      qc.setQueryData(['encounter', String(encounter.id)], (prev) => {
        if (!prev || !newRecord) return prev
        return {
          ...prev,
          bedside_test_records: [...(prev.bedside_test_records || []), newRecord],
        }
      })
      qc.invalidateQueries({ queryKey: ['encounter', String(encounter.id)] })
    },
    onError: (err) => {
      setFormError(err)
    },
  })

  const existing = encounter.bedside_test_records || []

  // Pre-compute the billable subtotal so the doctor sees what the patient
  // will be charged across all bedside tests so far. Snapshotted prices
  // don't drift even if the rate card is edited mid-encounter.
  const subtotalPGK = existing.reduce((acc, r) => {
    const n = Number(r.unit_price_pgk)
    return Number.isFinite(n) ? acc + n : acc
  }, 0)

  return (
    <Card>
      <CardHeader
        title="Bedside tests"
        subtitle="Point-of-care tests performed during the encounter. Each row is billable and (where eligible) counts toward corporate medical-screening tallies."
        right={existing.length > 0 ? (
          <span className="text-[11px] uppercase tracking-wider text-slate-500">
            Subtotal <span className="font-mono text-slate-800 font-semibold">{fmtPGK(subtotalPGK) || '—'}</span>
          </span>
        ) : null}
      />

      {existing.length > 0 && (
        <ul className="divide-y divide-slate-100 mb-4 -mx-2">
          {existing.map((rec) => (
            <BedsideRow
              key={rec.id}
              record={rec}
              encounterId={String(encounter.id)}
              locked={locked}
              onChange={() => qc.invalidateQueries({ queryKey: ['encounter', String(encounter.id)] })}
            />
          ))}
        </ul>
      )}

      {locked ? (
        <div className="text-xs text-slate-400">
          {existing.length === 0
            ? 'No bedside tests were recorded on this encounter.'
            : 'Encounter is locked. Add new tests via an amended encounter.'}
        </div>
      ) : (
        <div>
          <label className="block">
            <span className="text-[11px] uppercase tracking-wide text-slate-500">
              Perform a bedside test
            </span>
            <div className="mt-1">
              <CatalogueAutocomplete
                queryKey="bedside-tests"
                searchFn={searchBedsideTests}
                placeholder="Search bedside tests (urine dip, ECG, cap glucose, HCG, peak flow…)"
                rowKey={(t) => t.code}
                renderRow={(t) => (
                  <>
                    <span className="font-mono text-xs text-wantok-700">{t.code}</span>
                    <span className="ml-2 font-medium">{t.name}</span>
                    {t.category && (
                      <span className="ml-2 text-[10px] uppercase border rounded-full px-1.5 py-0.5 bg-slate-50 text-slate-600 border-slate-200">
                        {t.category}
                      </span>
                    )}
                    {t.is_screening_eligible && (
                      <span className="ml-2 text-[10px] uppercase border rounded-full px-1.5 py-0.5 bg-emerald-50 text-emerald-700 border-emerald-200">
                        screening
                      </span>
                    )}
                    {t.price != null && (
                      <span className="ml-2 text-[10px] text-slate-500 font-mono">
                        {fmtPGK(t.price)}
                      </span>
                    )}
                  </>
                )}
                onPick={(t) => {
                  // Repeat bedside tests are clinically valid (BP repeat
                  // after rest, capillary glucose pre/post meal, urine dip
                  // pre/post fluids), so we don't block duplicates. Each
                  // row is distinguishable by its `performed_at` timestamp
                  // and bills separately.
                  setFormError(null)
                  if (t?.id == null) return  // defensive: catalogue rows always carry an id
                  create.mutate({
                    catalogId: t.id,
                    result_value: '',
                    abnormal: false,
                    notes: '',
                  })
                }}
              />
            </div>
          </label>

          {(formError || create.error) && (
            <div className="mt-3"><ErrorBanner error={formError || create.error} /></div>
          )}

          <p className="text-[11px] text-slate-400 mt-2">
            Tip — picking a test creates the row immediately. Enter the result inline below; it autosaves on blur.
          </p>
        </div>
      )}
    </Card>
  )
}

// ---------------------------------------------------------------------------
// BedsideRow — one existing record, inline editable.
//
// On blur, PATCH the changed fields. Snapshotted price + unit are read-only
// — the server controls them. Delete confirms before sending.
// ---------------------------------------------------------------------------

function BedsideRow({ record, encounterId, locked, onChange }) {
  const qc = useQueryClient()
  const [resultValue, setResultValue] = useState(record.result_value || '')
  const [abnormal, setAbnormal] = useState(Boolean(record.abnormal))
  const [notes, setNotes] = useState(record.notes || '')
  // Two-step delete confirmation. First click arms the button (turns red,
  // text changes to "Click again to delete"); second click within 4
  // seconds actually fires. Replaces window.confirm() which some browsers
  // suppress on programmatic invocations.
  const [delArmed, setDelArmed] = useState(false)

  const update = useMutation({
    mutationFn: (patch) => updateBedsideTestRecord(record.id, patch),
    onSuccess: (updated) => {
      // Optimistic merge so the row state matches the server response
      // without forcing a full encounter refetch.
      qc.setQueryData(['encounter', String(encounterId)], (prev) => {
        if (!prev || !updated) return prev
        return {
          ...prev,
          bedside_test_records: (prev.bedside_test_records || []).map(
            (r) => (r.id === updated.id ? updated : r)
          ),
        }
      })
      onChange?.()
    },
  })

  const del = useMutation({
    mutationFn: () => deleteBedsideTestRecord(record.id),
    onSuccess: () => {
      qc.setQueryData(['encounter', String(encounterId)], (prev) => {
        if (!prev) return prev
        return {
          ...prev,
          bedside_test_records: (prev.bedside_test_records || []).filter(
            (r) => r.id !== record.id
          ),
        }
      })
      onChange?.()
    },
    onError: (err) => {
      // Surface the failure inline so the user isn't left wondering
      // why the row stayed. Browser DevTools Network tab shows the
      // full response; this banner shows just the key fields.
      // eslint-disable-next-line no-console
      console.error('Bedside-test delete failed', err)
    },
  })

  function commitIfDirty() {
    if (locked) return
    const patch = {}
    if (resultValue !== (record.result_value || '')) patch.result_value = resultValue
    if (Boolean(abnormal) !== Boolean(record.abnormal)) patch.abnormal = Boolean(abnormal)
    if (notes !== (record.notes || '')) patch.notes = notes
    if (Object.keys(patch).length > 0) update.mutate(patch)
  }

  const priceText = fmtPGK(record.unit_price_pgk)

  return (
    <li className="px-2 py-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2 mb-1.5">
        <div className="flex items-center gap-2 flex-wrap min-w-0">
          <span className="font-mono text-xs text-wantok-700">{record.test_code}</span>
          <span className="font-semibold text-slate-800">{record.test_name}</span>
          {record.test_category && (
            <span className="text-[10px] uppercase border rounded-full px-1.5 py-0.5 bg-slate-50 text-slate-600 border-slate-200">
              {record.test_category}
            </span>
          )}
          {record.is_screening_eligible && (
            <span className="text-[10px] uppercase border rounded-full px-1.5 py-0.5 bg-emerald-50 text-emerald-700 border-emerald-200">
              screening
            </span>
          )}
          {abnormal && (
            <span className="text-[10px] uppercase border rounded-full px-1.5 py-0.5 bg-rose-50 text-rose-700 border-rose-200 font-bold">
              abnormal
            </span>
          )}
        </div>
        <div className="flex items-center gap-3 text-[11px] text-slate-500">
          {priceText && (
            <span title="Billable to the patient / org rate card snapshot.">
              <span className="uppercase tracking-wide text-slate-400 mr-1">Billed</span>
              <span className="font-mono text-slate-800 font-semibold">{priceText}</span>
            </span>
          )}
          {record.performed_at && (
            <span>{formatDateTime(record.performed_at)}</span>
          )}
          {record.performed_by_name && <span>· {record.performed_by_name}</span>}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-start">
        <div className="sm:col-span-5">
          <span className="text-[10px] uppercase tracking-wide text-slate-500">Result</span>
          <div className="flex items-stretch">
            <input
              type="text"
              value={resultValue}
              disabled={locked}
              onChange={(e) => setResultValue(e.target.value)}
              onBlur={commitIfDirty}
              placeholder={
                record.test_code === 'ECG12L' ? 'e.g. sinus rhythm, no acute changes'
                : record.test_code === 'UDIP'  ? 'e.g. trace blood, leucs +, nitrites neg'
                : 'Result'
              }
              className="flex-1 border rounded-l px-2 py-1.5 text-sm disabled:bg-slate-50"
            />
            {record.result_unit && (
              <span className="border border-l-0 rounded-r bg-slate-50 px-2 py-1.5 text-xs text-slate-500 self-stretch flex items-center font-mono">
                {record.result_unit}
              </span>
            )}
          </div>
        </div>

        <label className="sm:col-span-2 flex items-end gap-2 pb-2 pl-1">
          <input
            type="checkbox"
            checked={abnormal}
            disabled={locked}
            onChange={(e) => {
              setAbnormal(e.target.checked)
              // Toggle commits immediately — abnormality flag matters for
              // the doctor's at-a-glance scan; no point waiting for blur.
              if (!locked) update.mutate({ abnormal: e.target.checked })
            }}
          />
          <span className="text-xs text-slate-700">Abnormal</span>
        </label>

        <div className="sm:col-span-5">
          <span className="text-[10px] uppercase tracking-wide text-slate-500">Notes (optional)</span>
          <input
            type="text"
            value={notes}
            disabled={locked}
            onChange={(e) => setNotes(e.target.value)}
            onBlur={commitIfDirty}
            placeholder="Context, technique, follow-up plan"
            className="w-full border rounded px-2 py-1.5 text-sm disabled:bg-slate-50"
          />
        </div>
      </div>

      {!locked && (
        <div className="flex justify-end mt-1.5 items-center gap-2">
          {delArmed && (
            <button
              type="button"
              onClick={() => setDelArmed(false)}
              disabled={del.isPending}
              className="text-[11px] text-slate-500 hover:underline disabled:opacity-60"
            >
              Cancel
            </button>
          )}
          <button
            type="button"
            onClick={() => {
              if (del.isPending) return
              if (!delArmed) {
                // First click: arm the button and auto-disarm in 4s so it
                // doesn't stay "armed" if the doctor walks away from the row.
                setDelArmed(true)
                setTimeout(() => setDelArmed(false), 4000)
                return
              }
              // Second click within 4s: actually delete.
              setDelArmed(false)
              del.mutate()
            }}
            disabled={del.isPending}
            className={
              'text-[11px] rounded px-2 py-0.5 transition disabled:opacity-60 ' +
              (delArmed
                ? 'bg-rose-600 hover:bg-rose-700 text-white font-semibold'
                : 'text-rose-600 hover:underline')
            }
            title={delArmed ? 'Click again to confirm — this deletes the billing line.' : 'Remove this bedside test from the encounter.'}
          >
            {del.isPending
              ? 'Removing…'
              : delArmed
                ? 'Click again to delete'
                : 'Remove'}
          </button>
        </div>
      )}
      {del.isError && (
        <div className="mt-2 text-[11px] bg-rose-50 border border-rose-200 text-rose-800 rounded px-2 py-1">
          <span className="font-semibold">Delete failed.</span>{' '}
          {del.error?.response?.status && (
            <span className="font-mono">HTTP {del.error.response.status}</span>
          )}{' '}
          {(() => {
            const r = del.error?.response?.data
            if (!r) return del.error?.message || ''
            if (typeof r === 'string') return r
            if (r.detail) return r.detail
            return JSON.stringify(r)
          })()}
        </div>
      )}
    </li>
  )
}
